// src/Contexts/MidiContext.tsx
/* eslint-disable react-refresh/only-export-components -- context file */
import React, { useCallback } from 'react';
import { detectDevice, midiPitchToFrequency } from 'Utils';
import { useEffect, useState, useRef } from 'react';
import * as eventAction from 'Actions/events';
import * as Tone from 'tone';
import { UseStateError } from 'Utils/Errors';
import { useSelector, useDispatch } from 'react-redux';
import { MainAppReducer } from 'Types';
import logger from 'Utils/Logger';
import { isCapacitorNative, MIDI_BLOCKER_PATHS } from 'Utils/Constants';
import { Box, IconButton } from '@mui/material';
import KeyboardConnectMidi from 'assets/images/KeyboardConnectMidi.png';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import * as lessonActions from 'Actions/lesson';
import * as tutorialActions from 'Actions/tutorial';
import * as repertoireActions from 'Actions/repertoire';
import { isSafariBrowser } from 'Utils/UserAgent';
import { awsRum } from 'Utils/AwsRum';
import MuseFlowAudio from 'Utils/MuseFlowAudioPlugin';
import Logger from 'Utils/Logger';

// Define NoteMessage type (was from @midival/core)
export type NoteMessage = {
  note: number;
  velocity: number;
  channel: number;
  command?: number;
  data1?: number;
  data2?: number;
};

// Type for tracking connected MIDI inputs
type MidiInputInfo = {
  id: string;
  name: string;
  input: MIDIInput;
};

// Global MIDI state that persists across React re-renders/refreshes
// This prevents the hanging issue where requestMIDIAccess never resolves on refresh
let globalMidiAccess: MIDIAccess | null = null;
let globalMidiPromise: Promise<MIDIAccess> | null = null;
/// Change the input on and off events to be react callbacks
// so that a change in state playToneSound will be reflected
// Will need to add methods to change playToneSound similar to
// functions defined in Authentication.tsx

export type MidiProviderProps = {
  children?: React.ReactNode;
  location?: { pathname: string };
  navigate?: (path: string) => void;
  sampler?: Tone.Sampler;
};

export type MidiStateType = {
  showMidiModal: boolean;
  dismissedMidiModal: boolean;
};

export type MidiFunctions = {
  addListener: (listener: Listener) => void;
  removeListener: (listener: Listener) => void;
  clearListeners: () => void;
  isConnected: () => boolean;
  capMidiReceived: { on: string; off: string; other: unknown; connected: boolean };
  checkMidiAndShowModal: () => void;
  hideMidiModal: () => void;
};

export type Listener = {
  name: string;
  noteOn: (ev: NoteMessage) => void;
  noteOff: (ev: NoteMessage) => void;
};

export const MidiContext = React.createContext<(MidiStateType & MidiFunctions) | null>(null);

export const useMidiContext: () => MidiStateType & MidiFunctions = () => {
  const state = React.useContext(MidiContext);
  if (!state) {
    throw new UseStateError(useMidiContext, MidiProvider);
  }
  return {
    ...state,
  };
};

type MidiDeviceMap = { [key: string]: MidiInputInfo };

export const MidiProvider: React.FC<MidiProviderProps> = ({
  children,
  location,
  navigate,
  sampler,
}: MidiProviderProps) => {
  const [midiInitialized, setMidiInitialized] = useState(false);
  const [devices, setDevices] = useState<MidiDeviceMap>({});
  const [showMidiModal, setShowMidiModal] = useState(false);
  const [dismissedMidiModal, setDismissedMidiModal] = useState(false); // user manually closed
  const [midiDevicesChecked, setMidiDevicesChecked] = useState(false); // true after MIDI init completes

  const data = useSelector((state: MainAppReducer) => state.mainAppReducer);
  const listeners = React.useRef<Map<string, Listener>>(new Map());
  const dispatch = useDispatch();
  const [successEventSent, setSuccessEventSent] = React.useState(false);
  const restrict_midi = process.env.REACT_APP_REQUIRE_MIDI === 'true';
  const previousLocation = sessionStorage.getItem('previousLocation') || '';

  // const audioReady = (window as any).__audioUnlocked === true && Tone.getContext().rawContext?.state === "running";
  const audioReadyRef = useRef(false);

  useEffect(() => {
    const update = () => {
      audioReadyRef.current =
        (window as Window & { __audioUnlocked?: boolean }).__audioUnlocked === true &&
        (Tone.getContext().rawContext as AudioContext | undefined)?.state === 'running';
    };

    update();

    const onAudioReady = (e: Event) => {
      audioReadyRef.current = !!(e as CustomEvent<{ ready?: boolean }>).detail?.ready;
    };

    window.addEventListener('AudioReady', onAudioReady);
    document.addEventListener('visibilitychange', update);

    return () => {
      window.removeEventListener('AudioReady', onAudioReady);
      document.removeEventListener('visibilitychange', update);
    };
  }, []);

  const noteOff = React.useCallback(
    (ev: NoteMessage) => {
      // Only play sound if audio_on is explicitly true (not false or undefined)
      // Default to true if undefined (matches UI default), but respect false
      const audioOn = data?.userData?.audio_on !== undefined ? data.userData.audio_on : true;
      if (audioOn && audioReadyRef.current && !isCapacitorNative()) {
        try {
          sampler?.triggerRelease(midiPitchToFrequency(ev.note));
        } catch (error) {
          Logger.warn('Failed to trigger sampler release:', error);
          awsRum?.recordError(error as Error);
        }
      }
      for (const [, listener] of listeners.current) {
        listener.noteOff(ev);
      }
    },
    [sampler, data?.userData?.audio_on, listeners],
  );

  const noteOn = React.useCallback(
    (ev: NoteMessage) => {
      // sometimes midi keyboards will send note-on event with 0 velocity
      // we must treat this like a not off event instead
      // 254 is "active sensing" and can be ignored
      // Only play sound if audio_on is explicitly true (not false or undefined)
      // Default to true if undefined (matches UI default), but respect false
      const audioOn = data?.userData?.audio_on !== undefined ? data.userData.audio_on : true;
      setCapMidiReceived((prevData) => ({
        ...prevData,
        other: `Note on? audio_on: ${audioOn}; conditions:  ${
          ev.velocity !== 0 && ev.note !== 254
        } midiPitchToFrequency(ev.note): ${midiPitchToFrequency(
          ev.note,
        )}; ev object stringify: ${JSON.stringify(ev)}`,
      }));

      if (ev.velocity !== 0 && ev.note !== 254) {
        Logger.log(
          '[MidiContext] noteOn callback - note:',
          ev.note,
          'velocity:',
          ev.velocity,
          'listeners:',
          listeners.current.size,
        );
        const maxVolume = 1.0;
        // Only play sound if audio_on is explicitly true
        if (audioOn && audioReadyRef.current && !isCapacitorNative()) {
          try {
            sampler?.triggerAttack(
              midiPitchToFrequency(ev.note),
              Tone.immediate(),
              (ev.velocity / 128) * maxVolume,
            );
          } catch (error) {
            Logger.warn('Failed to trigger sampler attack:', error);
            awsRum?.recordError(error as Error);
          }
        }
        for (const [name, listener] of listeners.current) {
          Logger.log('[MidiContext] Calling listener:', name);
          listener.noteOn(ev);
        }
      } else if (ev.note !== 254) {
        noteOffRef.current(ev); // call the note off logic becasue velocity is 0.
      }
    },
    [data?.userData?.audio_on, sampler, noteOff, listeners],
  );

  const noteOnRef = useRef(noteOn);
  const noteOffRef = useRef(noteOff);
  useEffect(() => {
    noteOnRef.current = noteOn;
    noteOffRef.current = noteOff;
  }, [noteOn, noteOff]);

  const [capMidiReceived, setCapMidiReceived] = useState<{
    on: string;
    off: string;
    other: unknown;
    connected: boolean;
  }>({
    on: '--',
    off: '--',
    other: 0,
    connected: false,
  });
  const [initialized, setInitialized] = useState(false);

  const userData = data.userData;
  // To determine nav back & later option
  const inWelcomeFlow = !userData?.subscription_status;

  // Whether current route should block for MIDI (not in allowed list and flag enabled)
  const requiresMidiGate = React.useCallback(() => {
    // we dont want to cover the device blocker screen with the safari/mobile blocker
    const isIneligableDevice =
      isSafariBrowser() && !(detectDevice() === 'desktop') && !isCapacitorNative();

    const path = (location?.pathname || '').replace(/\/$/, ''); // normalize trailing slash

    // Robust match:
    // - exact match: "/repertoire-play"
    // - subroute match: "/repertoire-play/123"
    const isEligablePath = MIDI_BLOCKER_PATHS.some((p) => {
      const base = (p || '').replace(/\/$/, '');
      return path === base || path.startsWith(base + '/');
    });

    console.log('location.pathname', location?.pathname);
    console.debug({ restrict_midi, isIneligableDevice, isEligablePath });
    return restrict_midi && !isIneligableDevice && isEligablePath;
  }, [restrict_midi, location?.pathname]);

  const navConnect = useCallback(() => {
    logger.info('nav connect', location.pathname);
    if (requiresMidiGate()) {
      dispatch(eventAction.midiConnectionFailureEventAction() as Parameters<typeof dispatch>[0]);
      logger.info('showing midi modal (requiresMidiGate)');
      setShowMidiModal(true);
    }
  }, [location.pathname, dispatch, navigate, requiresMidiGate]);

  // Store midiAccess ref for statechange handler
  const midiAccessRef = useRef<MIDIAccess | null>(null);

  useEffect(() => {
    if (isCapacitorNative()) return;
    if (initialized) return;

    let cancelled = false;

    Logger.log('[MidiContext] Starting MIDI initialization with native Web MIDI API...');

    // Helper to parse MIDI messages
    const handleMidiMessage = (event: MIDIMessageEvent) => {
      const data = event.data;
      if (!data || data.length < 1) return;

      const status = data[0];
      const command = status & 0xf0;
      const channel = status & 0x0f;
      const note = data.length > 1 ? data[1] : 0;
      const velocity = data.length > 2 ? data[2] : 0;

      // Note On (0x90) with velocity > 0
      if (command === 0x90 && velocity > 0) {
        Logger.log('[MidiContext] Note On:', note, velocity);
        noteOnRef.current({
          note,
          velocity,
          channel,
          command,
          data1: note,
          data2: velocity,
        });
        return;
      }

      // Note Off (0x80) or Note On with velocity 0
      if (command === 0x80 || (command === 0x90 && velocity === 0)) {
        Logger.log('[MidiContext] Note Off:', note);
        noteOffRef.current({
          note,
          velocity,
          channel,
          command,
          data1: note,
          data2: velocity,
        });
        return;
      }
    };

    const connectDevice = (input: MIDIInput) => {
      // NOTE: We intentionally do NOT check `cancelled` here.
      // Device setup must proceed even if the effect was cleaned up,
      // otherwise MIDI won't work after StrictMode double-mounting.

      const inputId = input.id;
      const inputName = input.name || 'Unknown MIDI Device';

      Logger.log('[MidiContext] connectDevice called for:', inputId, inputName);

      setDevices((prev) => {
        if (prev[inputId]) {
          Logger.log('[MidiContext] Device already exists:', inputId);
          return prev;
        }

        Logger.log('[MidiContext] Adding new device:', inputId, inputName);
        logger.info('ADDING DEVICE', { id: inputId, name: inputName });

        // Set up the MIDI message handler
        input.onmidimessage = handleMidiMessage;

        return {
          ...prev,
          [inputId]: { id: inputId, name: inputName, input },
        };
      });
    };

    const disconnectDevice = (inputId: string) => {
      Logger.log('[MidiContext] ===========DEVICE DISCONNECT===============', inputId);
      logger.info('===========DEVICE DISCONNECT===============', inputId);

      setDevices((prev) => {
        const existing = prev[inputId];
        if (!existing) return prev;

        // Remove the message handler
        try {
          existing.input.onmidimessage = null;
        } catch (e) {
          Logger.log('[MidiContext] Error removing message handler:', e);
        }

        const next = { ...prev };
        delete next[inputId];
        return next;
      });
    };

    (async () => {
      try {
        // Check if Web MIDI API is available
        if (!navigator.requestMIDIAccess) {
          Logger.error('[MidiContext] Web MIDI API not available in this browser');
          navConnect();
          return;
        }

        Logger.log('[MidiContext] Calling navigator.requestMIDIAccess()...');
        Logger.log('[MidiContext] Current URL:', window.location.href);
        Logger.log('[MidiContext] Is secure context:', window.isSecureContext);
        Logger.log('[MidiContext] globalMidiAccess:', globalMidiAccess ? 'exists' : 'null');
        Logger.log('[MidiContext] globalMidiPromise:', globalMidiPromise ? 'exists' : 'null');
        logger.info('trying connect');

        // Add a small delay to let browser clean up from previous page
        // This helps prevent the hanging issue on refresh
        Logger.log('[MidiContext] Waiting 500ms before requesting MIDI...');
        await new Promise((resolve) => setTimeout(resolve, 500));

        if (cancelled) {
          Logger.log('[MidiContext] Cancelled during delay');
          return;
        }

        // Use global state to prevent multiple requests
        // This fixes the issue where requestMIDIAccess hangs on refresh
        let midiPromise: Promise<MIDIAccess>;

        if (globalMidiAccess) {
          // Already have MIDI access from before
          Logger.log('[MidiContext] Using existing globalMidiAccess');
          midiPromise = Promise.resolve(globalMidiAccess);
        } else if (globalMidiPromise) {
          // Request already in progress
          Logger.log('[MidiContext] Using existing globalMidiPromise');
          midiPromise = globalMidiPromise;
        } else {
          // Need to request new MIDI access
          Logger.log('[MidiContext] Creating new MIDI access request...');
          globalMidiPromise = navigator.requestMIDIAccess({ sysex: false });
          midiPromise = globalMidiPromise;

          // Store the access once it resolves
          midiPromise
            .then((access) => {
              globalMidiAccess = access;
              Logger.log('[MidiContext] Stored globalMidiAccess');
            })
            .catch(() => {
              // Clear the promise so we can retry
              globalMidiPromise = null;
            });
        }

        Logger.log('[MidiContext] Setting up .then() handler...');

        // Handle the MIDI access when/if it resolves (don't block on it)
        midiPromise.then(
          (midiAccess) => {
            // NOTE: We intentionally do NOT check `cancelled` here.
            // Even if the React effect was cleaned up, we still want to:
            // 1. Store the midiAccess globally
            // 2. Set up device handlers
            // This ensures MIDI works even after StrictMode double-mounting

            Logger.log('[MidiContext] MIDI access granted! cancelled =', cancelled);
            midiAccessRef.current = midiAccess;

            // Count inputs
            let inputCount = 0;
            midiAccess.inputs.forEach(() => inputCount++);
            Logger.log('[MidiContext] midiAccess.inputs count =', inputCount);
            logger.info('in connect');

            // Process initial devices
            Logger.log('[MidiContext] Processing initial devices...');
            midiAccess.inputs.forEach((input: MIDIInput) => {
              Logger.log('[MidiContext] Found initial input:', input.id, input.name);
              connectDevice(input);
            });

            // Listen for device connect/disconnect
            midiAccess.onstatechange = (event: MIDIConnectionEvent) => {
              const port = event.port;
              Logger.log(
                '[MidiContext] onstatechange:',
                port?.type,
                port?.id,
                port?.name,
                port?.state,
              );

              if (port?.type === 'input') {
                if (port.state === 'connected') {
                  Logger.log(
                    '[MidiContext] ===========DEVICE CONNECT===============',
                    port.id,
                    port.name,
                  );
                  logger.info('===========DEVICE CONNECT===============');
                  connectDevice(port as MIDIInput);
                  dispatch(
                    eventAction.midiConnectionSuccessEventAction() as Parameters<
                      typeof dispatch
                    >[0],
                  );
                  setShowMidiModal(false);
                } else if (port.state === 'disconnected') {
                  disconnectDevice(port.id);
                }
              }
            };

            // Check if we should show/hide modal based on devices
            let deviceCount = 0;
            midiAccess.inputs.forEach(() => deviceCount++);
            const hasAny = deviceCount > 0;
            Logger.log('[MidiContext] hasAny devices:', hasAny);

            // Mark that we've checked for devices (prevents modal flash)
            setMidiDevicesChecked(true);

            if (hasAny) {
              Logger.log('[MidiContext] Devices found, hiding modal');
              setShowMidiModal(false);
            } else if (requiresMidiGate()) {
              Logger.log('[MidiContext] No devices, showing modal');
              setShowMidiModal(true);
            }

            Logger.log('[MidiContext] MIDI initialization complete (async)');
          },
          (err) => {
            Logger.error('[MidiContext] MIDI access denied or failed:', err);
            // Don't block the app - just log the error
          },
        );

        // Don't wait - let the app continue
        // The MIDI will connect in the background when/if the browser allows it
        Logger.log('[MidiContext] Continuing without waiting for MIDI...');

        // DON'T show modal here immediately - wait for the .then() callback
        // to complete and check if devices were found. This prevents the
        // modal from flashing briefly before MIDI is detected.

        // All MIDI handling is done in the .then() callback above
        // Nothing else to do here - the effect will complete and
        // MIDI will connect asynchronously in the background
      } catch (e) {
        Logger.error('[MidiContext] requestMIDIAccess() failed:', e);
        navConnect();
      } finally {
        if (!cancelled) {
          Logger.log('[MidiContext] Setting initialized = true');
          setInitialized(true);
        }
      }
    })();

    return () => {
      Logger.log('[MidiContext] Cleanup running, setting cancelled = true');
      cancelled = true;
      // Clean up statechange handler
      if (midiAccessRef.current) {
        midiAccessRef.current.onstatechange = null;
      }
    };
  }, [initialized, dispatch, navConnect, requiresMidiGate]);

  // Define before first usage to avoid TDZ errors
  const isAnyDeviceConnected = React.useCallback(() => {
    return isCapacitorNative() ? !!capMidiReceived.connected : Object.keys(devices).length > 0;
  }, [capMidiReceived.connected, devices]);

  // Also hide modal when navigating to an allowed path
  useEffect(() => {
    if (showMidiModal && !requiresMidiGate()) {
      setShowMidiModal(false);
    }
  }, [location.pathname, showMidiModal, requiresMidiGate]);

  // Auto-hide modal as soon as a device is connected
  useEffect(() => {
    if (showMidiModal && isAnyDeviceConnected()) {
      setShowMidiModal(false);
    }
  }, [showMidiModal, isAnyDeviceConnected]);

  useEffect(() => {
    const onDevicesChanged = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      Logger.log('MIDI devices changed:', detail);
      const hasDevices = detail?.sources?.length > 0;
      Logger.log('[MidiContext] MIDI devices detected:', hasDevices, 'sources:', detail?.sources);
      setCapMidiReceived((prev) => ({ ...prev, connected: hasDevices }));
    };

    window.addEventListener('MIDIDevicesChanged', onDevicesChanged);
    document.addEventListener('MIDIDevicesChanged', onDevicesChanged as EventListener);

    // For Capacitor: Check for already-stored device state on mount
    // This ensures we detect devices that were already connected before React mounted
    if (isCapacitorNative()) {
      Logger.log('[MidiContext] Checking for initial MIDI device state...');
      const storedState = (window as Window & { __midiDeviceState?: unknown }).__midiDeviceState;
      if (storedState) {
        const hasDevices =
          typeof storedState.connected === 'boolean'
            ? storedState.connected
            : (storedState.sources?.length ?? 0) > 0;

        Logger.log('[MidiContext] Found stored MIDI device state:', storedState);
        setCapMidiReceived((prev) => ({ ...prev, connected: hasDevices }));
      }
      // Mark devices as checked for Capacitor (prevents modal flash)
      setMidiDevicesChecked(true);
    }

    return () => {
      window.removeEventListener('MIDIDevicesChanged', onDevicesChanged);
      document.removeEventListener('MIDIDevicesChanged', onDevicesChanged as EventListener);
    };
  }, []);

  useEffect(() => {
    if (!isCapacitorNative()) return; // function designed for capacitor.

    Logger.log('Adding listener for MIDIReceived...');
    Logger.log('What is the midi ?', data?.userData);

    const handleMidiReceived = (event: MIDIMessageEvent) => {
      const dt = new Date();
      const dateStr = `${dt.getHours()}:${String(dt.getMinutes()).padStart(2, '0')}`;
      const raw = Array.from(event?.detail?.data ?? []) as number[];
      if (!raw.length) return;

      // Set "connected" only when it flips from false -> true (prevents state storm)
      setCapMidiReceived((prev) => (prev.connected ? prev : { ...prev, connected: true }));

      // Helpers
      const REALTIME_MIN = 0xf8; // F8..FF
      const isRealtime = (b: number) => b >= REALTIME_MIN; // includes FA/FB/FC transport
      const isStatus = (b: number) => b >= 0x80 && b < 0xf0; // channel voice only
      const msgDataLen = (status: number) => {
        const hi = status & 0xf0;
        // Cn/Dn have 1 data byte; others (8n,9n,An,Bn,En) have 2
        return hi === 0xc0 || hi === 0xd0 ? 1 : 2;
      };

      // Persist running status across packets using a window property (no extra globals needed)
      let runningStatus: number | null =
        (window as Window & { __midiRunningStatus?: number | null }).__midiRunningStatus ?? null;

      // Fast path: a single real-time byte? ignore it entirely.
      if (raw.length === 1 && isRealtime(raw[0])) return;

      // Parse stream
      for (let i = 0; i < raw.length; ) {
        const b: number = raw[i];

        // Skip any real-time byte (they can appear between data bytes)
        if (isRealtime(b)) {
          i += 1;
          continue;
        }

        // New status byte?
        if (isStatus(b)) {
          runningStatus = b;
          (window as Window & { __midiRunningStatus?: number | null }).__midiRunningStatus =
            runningStatus; // stash for next callback
          i += 1;

          const needed = msgDataLen(runningStatus);
          if (i + needed > raw.length) {
            // Incomplete message in this packet; wait for next one
            break;
          }

          const d1 = raw[i];
          const d2 = needed === 2 ? raw[i + 1] : 0;

          handleChannelMessage(runningStatus, d1, d2);
          i += needed;
          continue;
        }

        // Data byte with running status
        if (runningStatus !== null) {
          const needed = msgDataLen(runningStatus);
          // We already have the first data byte at raw[i]
          if (needed === 2 && i + 1 >= raw.length) {
            // Incomplete; wait for the next packet to get the second data byte
            break;
          }
          const d1 = raw[i];
          const d2 = needed === 2 ? raw[i + 1] : 0;

          handleChannelMessage(runningStatus, d1, d2);
          i += needed;
          continue;
        }

        // Stray data byte with no running status — skip it
        i += 1;
      }

      function handleChannelMessage(status: number, data1: number, data2: number) {
        // NOTE ON (9n) with velocity > 0
        if ((status & 0xf0) === 0x90 && data2 > 0) {
          setCapMidiReceived((prev) => ({
            ...prev,
            on: `${dateStr} Note-On: [${status}, ${data1}, ${data2}]`,
          }));
          // Logger.log(`Note-On detected: Note ${data1}, Velocity ${data2}`);
          noteOnRef.current?.({
            channel: status & 0x0f,
            command: status & 0xf0,
            note: data1,
            velocity: data2,
            data1,
            data2,
          });
          return;
        }

        // NOTE OFF (8n) or Note-On with velocity 0
        if ((status & 0xf0) === 0x80 || ((status & 0xf0) === 0x90 && data2 === 0)) {
          setCapMidiReceived((prev) => ({
            ...prev,
            off: `${dateStr} Note-Off: [${status}, ${data1}, ${data2}]`,
          }));
          // Logger.log(`Note-Off detected: Note ${data1}, Velocity ${data2}`);
          noteOffRef.current?.({
            channel: status & 0x0f,
            command: status & 0xf0,
            note: data1,
            velocity: data2,
            data1,
            data2,
          });
          return;
        }

        // Control Change (Bn)
        if ((status & 0xf0) === 0xb0) {
          Logger.log(`Control Change: Controller ${data1}, Value ${data2}`);
          return;
        }
        // Program Change (Cn) or Channel Pressure (Dn) handled as “unhandled” for now
        if ((status & 0xf0) === 0xc0 || (status & 0xf0) === 0xd0) {
          Logger.log(`Channel msg ${status.toString(16)}: d1=${data1}`);
          return;
        }
        Logger.log(`Unhandled MIDI message: [${status}, ${data1}, ${data2}]`);
      }
    };

    document.addEventListener('MIDIReceived', handleMidiReceived as EventListener);

    return () => {
      logger.debug('Removing listener for MIDIReceived...');
      document.removeEventListener('MIDIReceived', handleMidiReceived as EventListener);
    };
  }, []);

  useEffect(() => {
    setMidiInitialized(false);
  }, [data?.userData?.audio_on]);

  useEffect(() => {
    if (midiInitialized == true && !successEventSent) {
      dispatch(eventAction.midiConnectionSuccessEventAction() as Parameters<typeof dispatch>[0]);
      setSuccessEventSent(true);
    }
  }, [midiInitialized, successEventSent]);

  useEffect(() => {
    if (isCapacitorNative()) {
      const audioOn = data?.userData?.audio_on !== undefined ? data.userData.audio_on : true;
      MuseFlowAudio.setPianoEnabled({ enabled: audioOn });
    }
  }, [data?.userData?.audio_on]);

  const addListener = React.useCallback(
    (listener: Listener) => {
      Logger.log(
        '[MidiContext] addListener:',
        listener.name,
        'current size:',
        listeners.current.size,
      );
      logger.info('adding listener', listener);
      if (listeners.current.has(listener.name)) {
        Logger.log('[MidiContext] Replacing existing listener:', listener.name);
      }
      listeners.current.delete(listener.name);
      listeners.current.set(listener.name, listener);
      Logger.log('[MidiContext] After add, size:', listeners.current.size);
    },
    [listeners],
  );

  const removeListener = React.useCallback(
    (listener: Listener) => {
      Logger.log('[MidiContext] removeListener:', listener.name);
      listeners.current.delete(listener.name);
      Logger.log('[MidiContext] After remove, size:', listeners.current.size);
    },
    [listeners],
  );

  const clearListeners = React.useCallback(() => {
    logger.info('clearing listeners');
    for (const [key] of listeners.current) {
      listeners.current.delete(key);
    }
  }, [listeners.current]);

  const isConnected = React.useCallback(() => {
    return midiInitialized;
  }, [midiInitialized]);

  const checkMidiAndShowModal = React.useCallback(() => {
    // Don't show modal until we've confirmed MIDI device state.
    // On page reload, MIDI init is async — without this guard
    // the modal flashes briefly (no devices yet) then auto-hides
    // once MIDI resolves, disrupting the exercise setup flow.
    if (!isCapacitorNative() && !midiDevicesChecked) return;

    Logger.log('checking midi and show modal', !isAnyDeviceConnected() && requiresMidiGate());
    if (!isAnyDeviceConnected() && requiresMidiGate()) {
      setShowMidiModal(true);
    }
  }, [isAnyDeviceConnected, requiresMidiGate, midiDevicesChecked]);

  useEffect(() => {
    // Don't show modal until we've checked for MIDI devices (prevents flash)
    if (!isCapacitorNative() && !midiDevicesChecked) return;

    if (!requiresMidiGate()) {
      if (showMidiModal) setShowMidiModal(false);
      return;
    }
    if (isAnyDeviceConnected()) {
      if (showMidiModal) setShowMidiModal(false);
      return;
    }
    if (dismissedMidiModal) return;
    dispatch(eventAction.midiConnectionFailureEventAction() as Parameters<typeof dispatch>[0]);
    if (
      location.pathname !== '/tutorial' ||
      data?.levelData[data?.levelSelect]?.level_number != 0
    ) {
      setShowMidiModal(true);
    }
  }, [
    location.pathname,
    requiresMidiGate,
    isAnyDeviceConnected,
    dismissedMidiModal,
    showMidiModal,
    dispatch,
    data?.levelData,
    data?.levelSelect,
    midiDevicesChecked,
  ]);

  const hideMidiModal = React.useCallback(() => setShowMidiModal(false), []);

  useEffect(() => {
    if (showMidiModal && location.pathname !== '/tutorial') {
      Logger.log('??????????????????????????????');
      setTimeout(() => {
        // need to wait for the scene to load before stopping playback
        Logger.log('showing midi modal, stopping playback');
        dispatch(lessonActions.setLessonPlaying({ lessonPlaying: false }));
        console.log('[LOOP-DBG] setRepertoirePlaying(false) from MidiContext showMidiModal');
        dispatch(repertoireActions.setRepertoirePlaying({ repertoirePlaying: false }));
        dispatch(tutorialActions.setManualPause({ manualPause: true }));
      }, 1000);
    }
  }, [showMidiModal, location.pathname]);

  // Reset dismissal when navigating to a different path so gate can appear again.
  useEffect(() => {
    setDismissedMidiModal(false);
  }, [location.pathname]);

  useEffect(() => {
    if (isCapacitorNative()) return;
    Logger.log('devices', devices);
    if (Object.keys(devices).length === 0) setDismissedMidiModal(false);
  }, [devices]);

  // On route change, enforce MIDI gate: if the new page requires MIDI and no device is connected, show the modal.
  useEffect(() => {
    // Don't show modal until we've checked for MIDI devices (prevents flash)
    if (!isCapacitorNative() && !midiDevicesChecked) return;

    if (requiresMidiGate()) {
      if (!isAnyDeviceConnected()) {
        if (!showMidiModal && !dismissedMidiModal) {
          dispatch(
            eventAction.midiConnectionFailureEventAction() as Parameters<typeof dispatch>[0],
          );
          // level 0 handles its own midi blocker page, which fires after the first chapter,
          // in order to give new users a chance to see what the app is like before being blocked
          // by the midi gate.
          if (
            location.pathname !== '/tutorial' ||
            data?.levelData[data?.levelSelect]?.level_number != 0
          ) {
            setShowMidiModal(true);
          }
        }
      } else if (showMidiModal) {
        setShowMidiModal(false);
      }
    } else if (showMidiModal) {
      setShowMidiModal(false);
    }
  }, [
    location.pathname,
    requiresMidiGate,
    isAnyDeviceConnected,
    showMidiModal,
    dispatch,
    dismissedMidiModal,
    midiDevicesChecked,
  ]);

  return (
    <MidiContext.Provider
      value={{
        addListener,
        removeListener,
        clearListeners,
        isConnected,
        capMidiReceived: capMidiReceived,
        checkMidiAndShowModal,
        hideMidiModal,
        showMidiModal,
        dismissedMidiModal, // expose in context
      }}
    >
      {children}
      {showMidiModal && (
        <>
          {/* Transparent backdrop to block clicks but still show app underneath */}
          <Box
            sx={{
              position: 'fixed',
              inset: 0,
              width: '100vw',
              height: '100vh',
              background: 'rgba(4,11,19,0.78)',
              zIndex: 3000,
            }}
          />
          {/* Back to roadmap button */}

          <Box
            sx={{
              position: 'fixed',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 3001,
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: 'min(900px, 90vw)',
                minHeight: '32vh',
                borderRadius: '8px',
                background: 'transparent',
                px: { xs: 4, md: 7 },
                py: { xs: 6, md: 8 },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                  background: 'rgba(4, 11, 19, 0.92)',
                  borderRadius: '12px',
                  px: { xs: 3, md: 4 },
                  py: { xs: 2.5, md: 3 },
                  mb: 3,
                  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.45)',
                  backdropFilter: 'blur(2px)',
                }}
              >
                <Box sx={{ position: 'fixed', top: 12, left: 12, zIndex: 3002 }}>
                  <IconButton
                    aria-label={
                      location?.pathname?.includes('/repertoire-play')
                        ? 'Back to repertoire'
                        : 'Back to roadmap'
                    }
                    onClick={() => {
                      const dest = location?.pathname?.includes('/repertoire-play')
                        ? '/repertoire'
                        : '/roadmap';
                      Logger.log(`[MUS-61] MidiContext disconnect button → ${dest}`);
                      navigate(dest);
                    }}
                    sx={{
                      color: '#CED0D4',
                      backgroundColor: 'rgba(4,11,19,0.6)',
                      '&:hover': { backgroundColor: 'rgba(4,11,19,0.8)' },
                    }}
                  >
                    <ArrowBackIcon />
                  </IconButton>
                </Box>
                <Box sx={{ position: 'fixed', top: 12, right: 12, zIndex: 3002 }}>
                  <IconButton
                    aria-label="Close"
                    onClick={() => {
                      setShowMidiModal(false);
                      setDismissedMidiModal(true);
                    }}
                    sx={{
                      color: '#CED0D4',
                      backgroundColor: 'rgba(4,11,19,0.6)',
                      '&:hover': { backgroundColor: 'rgba(4,11,19,0.8)' },
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
                <Box
                  sx={{
                    fontWeight: 400,
                    fontSize: { xs: '28px', md: '36px' },
                    color: '#CED0D4',
                    textAlign: 'center',
                  }}
                >
                  Connect your MIDI keyboard
                </Box>
                <Box
                  sx={{
                    fontWeight: 400,
                    fontSize: { xs: '18px', md: '20px' },
                    color: '#ADB1B7',
                    textAlign: 'center',
                    maxWidth: 760,
                    lineHeight: 1.5,
                  }}
                >
                  We didn’t detect a MIDI device. Plug in and turn on your keyboard. This message
                  will close automatically once connected.
                </Box>
                <Box sx={{ height: { xs: '180px', md: '240px' }, mt: 1 }}>
                  <img style={{ maxHeight: '100%' }} src={KeyboardConnectMidi} alt="connect-midi" />
                </Box>
              </Box>
            </Box>
          </Box>
        </>
      )}
    </MidiContext.Provider>
  );
};
